import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination, fmtId } from './ReportLayout';
import { ReportChartRow, BarCard, DonutCard, groupCount, groupByMonth } from './ReportCharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useShiftTypes } from '../../hooks/useSettings';
import { fmtTime12 } from '../../utils/time';

const fmtDate = v => { try { return v ? format(parseISO(v), 'd MMM yyyy') : '–'; } catch { return v||'–'; } };

const STATUS_COLOR = {
  completed: 'bg-green-50 text-green-700',
  in_progress: 'bg-blue-50 text-blue-700',
  pending: 'bg-amber-50 text-amber-700',
  cancelled: 'bg-red-50 text-red-600',
};

const COLS = [
  { key:'id',               label:'ID',          render: fmtId },
  { key:'shift_date',       label:'Date',         render: fmtDate },
  { key:'start_time',       label:'Start',        render: v => v ? fmtTime12(v) : '–' },
  { key:'end_time',         label:'End',          render: v => v ? fmtTime12(v) : '–' },
  { key:'shift_type',       label:'Type' },
  { key:'status',           label:'Status',       render: v => <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_COLOR[v]||'bg-slate-100 text-slate-600')}>{v||'–'}</span> },
  { key:'staff_name',       label:'Staff' },
  { key:'clients',          label:'Clients' },
  { key:'hours',            label:'Hours' },
  { key:'break_minutes',    label:'Break (min)' },
  { key:'price_book',       label:'Price Book' },
  { key:'is_recurring',     label:'Recurring',    render: v => v ? 'Yes' : 'No' },
  { key:'mileage_submitted',label:'Mileage',      render: v => v ? `${v} km` : '–' },
  { key:'address',          label:'Address' },
];

export default function ShiftDetails() {
  const [data, setData]       = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]     = useState(format(new Date(), 'yyyy-MM-dd'));
  const [clientId, setClientId]   = useState('');
  const [staffId, setStaffId]     = useState('');
  const [shiftType, setShiftType] = useState('');
  const shiftTypes = useShiftTypes();
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : r.data?.clients||[])).catch(()=>{});
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : r.data?.users||[])).catch(()=>{});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (clientId)  p.set('client_id', clientId);
    if (staffId)   p.set('staff_id', staffId);
    if (shiftType) p.set('shift_type', shiftType);
    api.get(`/reports/shift-details?${p}`)
      .then(r => { setData(r.data||[]); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, clientId, staffId, shiftType]);

  useEffect(() => { load(); }, [load]);

  const selClient = clients.find(c => c.id === clientId);
  const selStaff  = staff.find(s => s.id === staffId);
  const chips = [
    { label:'Date Range', value:`${startDate} - ${endDate}` },
    ...(selClient   ? [{ label:'Client',     value:`${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
    ...(selStaff    ? [{ label:'Staff',      value:`${selStaff.first_name} ${selStaff.last_name}`,   onRemove: () => setStaffId('') }] : []),
    ...(shiftType   ? [{ label:'Shift Type', value: shiftType,                                        onRemove: () => setShiftType('') }] : []),
  ];

  return (
    <ReportLayout title="Shift Details" data={data} filename="shift_details"
      columns={COLS} activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v=>!v)}
      chart={
        <ReportChartRow>
          <DonutCard title="Shifts by status" data={groupCount(data, 'status')} />
          <BarCard title="Shifts by month" data={groupByMonth(data, 'shift_date')} color="#4F46E5" />
        </ReportChartRow>
      }
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Staff</label>
            <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Shift Type</label>
            <select value={shiftType} onChange={e => setShiftType(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Types</option>
              {shiftTypes.filter(t => t.is_active).map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select></div>
          <div className="flex items-end"><button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button></div>
        </div>
      }>
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination}/>
    </ReportLayout>
  );
}
